// API service for Ventura Machina backend
// Supports both real API and demo mode

import type {
  SearchRequest,
  SearchResponse,
  ImageMetadata,
  Vehicle,
  ComparisonPair,
} from '@/types';
import type {
  SearchApiRequest,
  SearchApiResponse,
  ShowcaseApiResponse,
  CompareApiResponse,
  VehiclesApiResponse,
  ImageApiData,
} from '@/types/api';
import { transformImageData, transformVehicleData } from '@/types/api';
import * as demoApi from './demoApi';
import { useAuthStore } from '@/store/authStore';
import {
  setTokenProvider as setSharedTokenProvider,
  getAuthToken,
} from '@/lib/auth-token';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const API_TIMEOUT = 10000; // 10 seconds

// Re-export so TokenSync's existing import keeps working
export const setTokenProvider = setSharedTokenProvider;

/**
 * Check if running in demo mode
 * Demo mode is enabled via VITE_DEMO_MODE=true environment variable only
 * This ensures real API is used by default when pointing to ventura-vault
 */
export function isDemoMode(): boolean {
  return import.meta.env.VITE_DEMO_MODE === 'true';
}

/**
 * Enable demo mode
 */
export function enableDemoMode(): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('demoMode', 'true');
  }
}

/**
 * Disable demo mode
 */
export function disableDemoMode(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('demoMode');
  }
}

/**
 * Custom API error class
 */
export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Base fetch wrapper with timeout, error handling, and Bearer token injection.
 *
 * Token priority:
 *   1. Kiosk token from authStore (kiosk/production mode) — vm_kiosk_ prefix
 *   2. Clerk JWT from _getToken (Clerk/development mode)
 *
 * On 401 with a kiosk token: the device has been revoked. Clear auth state
 * so the app shows the pairing screen (or disconnected message).
 * On 401 with a Clerk token: retry once with a fresh token.
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  _isRetry: boolean = false
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

  try {
    // Prefer kiosk token (production pairing mode) over Clerk JWT
    const kioskToken = useAuthStore.getState().kioskToken;
    const token = await getAuthToken();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

    // Handle 401 based on auth type
    if (response.status === 401) {
      if (kioskToken) {
        // Kiosk token was revoked: clear auth so app shows pairing screen
        useAuthStore.getState().clearAuth();
        throw new ApiError(401, 'Device disconnected. Please re-pair the kiosk.');
      }
      // Clerk token expired: retry once with a fresh token
      if (!_isRetry) {
        clearTimeout(timeoutId);
        const freshToken = await getAuthToken();
        if (freshToken) {
          return apiFetch(endpoint, options, true);
        }
      }
    }

    // Clerk 404-on-missing-token fallback retry
    if (response.status === 404 && !token && !_isRetry) {
      clearTimeout(timeoutId);
      const freshToken = await getAuthToken();
      if (freshToken) {
        return apiFetch(endpoint, options, true);
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        (errorData as { message?: string }).message || `API error: ${response.statusText}`,
        (errorData as { details?: Record<string, unknown> }).details
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Request timeout');
    }
    throw new ApiError(500, 'Network error');
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Search for images using natural language query
 */
export async function searchImages(
  request: SearchRequest
): Promise<SearchResponse> {
  // Use demo API in demo mode
  if (isDemoMode()) {
    return demoApi.searchImages(request);
  }

  // Extract vehicle_id and make/model from scope
  let vehicleId = request.vehicleId;
  let make: string | undefined;
  let model: string | undefined;

  if (request.scope) {
    switch (request.scope.type) {
      case 'project':
        if (!vehicleId) vehicleId = request.scope.projectId;
        break;
      case 'make':
        make = request.scope.make;
        break;
      case 'model':
        make = request.scope.make;
        model = request.scope.model;
        break;
    }
  }

  const apiRequest: SearchApiRequest = {
    query: request.query,
    vehicle_id: vehicleId,
    make,
    model,
    filters: request.filters
      ? {
          component: request.filters.component,
          condition: request.filters.condition,
          phase: request.filters.phase,
          date_range: request.filters.dateRange,
        }
      : undefined,
    limit: request.limit || 20,
    offset: request.offset || 0,
  };

  const response = await apiFetch<SearchApiResponse>('/search', {
    method: 'POST',
    body: JSON.stringify(apiRequest),
  });

  return {
    query: response.query,
    results: response.results.map(transformImageData),
    totalCount: response.total_count,
    hasMore: response.has_more,
    processingTimeMs: response.processing_time_ms,
    suggestedFilters: response.suggested_filters,
  };
}

/**
 * Get a single image by ID
 */
export async function getImage(
  imageId: string,
  size: 'thumb' | 'medium' | 'full' = 'full'
): Promise<ImageMetadata | null> {
  // Use demo API in demo mode
  if (isDemoMode()) {
    return demoApi.getImage(imageId);
  }

  const response = await apiFetch<ImageApiData>(
    `/image/${imageId}?size=${size}`
  );
  return transformImageData(response);
}

/**
 * Get showcase/hero images with scope filtering
 */
export async function getShowcaseImages(
  vehicleId?: string,
  scope?: {
    type: 'all' | 'make' | 'model' | 'project';
    make?: string;
    model?: string;
    projectId?: string;
  }
): Promise<{ vehicle?: Vehicle; heroImages: ImageMetadata[] }> {
  // Use demo API in demo mode
  if (isDemoMode()) {
    const heroImages = await demoApi.getShowcaseImages(scope);
    if (vehicleId) {
      const vehicle = await demoApi.getVehicle(vehicleId);
      return { vehicle: vehicle || undefined, heroImages };
    }
    return { heroImages };
  }

  // When we have a specific vehicleId, use the per-vehicle endpoint
  if (vehicleId) {
    const response = await apiFetch<ShowcaseApiResponse>(
      `/showcase/${vehicleId}`
    );
    return {
      vehicle: transformVehicleData(response.vehicle),
      heroImages: response.hero_images.map(transformImageData),
    };
  }

  // Otherwise use the scope-aware endpoint
  const params = new URLSearchParams();
  if (scope) {
    params.set('scope_type', scope.type);
    if (scope.make) params.set('make', scope.make);
    if (scope.model) params.set('model', scope.model);
    if (scope.projectId) params.set('project_id', scope.projectId);
  }
  const response = await apiFetch<{ hero_images: ImageApiData[] }>(
    `/showcase?${params.toString()}`
  );
  return {
    heroImages: response.hero_images.map(transformImageData),
  };
}

/**
 * Get comparison data for two images
 */
export async function getComparison(
  imageId1: string,
  imageId2?: string
): Promise<ComparisonPair | null> {
  // Use demo API in demo mode
  if (isDemoMode()) {
    return demoApi.getComparison(imageId1);
  }

  const response = await apiFetch<CompareApiResponse>(
    `/compare/${imageId1}/${imageId2}`
  );
  return {
    beforeImage: transformImageData(response.before_image),
    afterImage: transformImageData(response.after_image),
    component: response.component,
  };
}

/**
 * Get all vehicles/projects
 */
export async function getVehicles(): Promise<Vehicle[]> {
  // Use demo API in demo mode
  if (isDemoMode()) {
    return demoApi.getVehicles();
  }

  const response = await apiFetch<VehiclesApiResponse>('/vehicles');
  return response.vehicles.map(transformVehicleData);
}

/**
 * Get similar images to a given image
 */
export async function getSimilarImages(
  imageId: string,
  limit: number = 10
): Promise<ImageMetadata[]> {
  // Use demo API in demo mode
  if (isDemoMode()) {
    return demoApi.getSimilarImages(imageId, limit);
  }

  const response = await apiFetch<{ images: ImageApiData[] }>(
    `/image/${imageId}/similar?limit=${limit}`
  );
  return response.images.map(transformImageData);
}

/**
 * Get before/after pairs for a component
 */
export async function getComparisonPairs(
  vehicleId?: string,
  component?: string
): Promise<ComparisonPair[]> {
  // Use demo API in demo mode
  if (isDemoMode()) {
    return demoApi.getAllComparisons();
  }

  const params = new URLSearchParams();
  if (component) params.set('component', component);

  const response = await apiFetch<{ pairs: CompareApiResponse[] }>(
    `/vehicles/${vehicleId}/comparisons?${params}`
  );

  return response.pairs.map((pair) => ({
    beforeImage: transformImageData(pair.before_image),
    afterImage: transformImageData(pair.after_image),
    component: pair.component,
  }));
}

/**
 * Get filter options for search UI
 */
export async function getFilterOptions() {
  // Use demo API in demo mode
  if (isDemoMode()) {
    return demoApi.getFilterOptions();
  }

  // Real API would return filter options
  return apiFetch<{
    components: string[];
    conditions: string[];
    vehicles: { id: string; name: string }[];
    tags: string[];
  }>('/filters');
}
